export const getDriverApprovalStatus = (driver = {}, strict = false) => {
  const statuses = [driver.driverDocumentStatus, driver.approvalStatus, driver.driverApprovalStatus]
    .filter(Boolean).map((status) => String(status).trim().toUpperCase());
  if (statuses.includes('REJECTED')) return 'REJECTED';
  if (strict && statuses.some(status => ['PENDING', 'NOT_SUBMITTED', 'SUBMITTED', 'IN_REVIEW', 'UNDER_REVIEW'].includes(status))) return 'PENDING';
  if ((strict && (driver.isApproved === true || driver.approved === true || driver.documentsApproved === true)) || driver.driverDocumentsApproved === true || statuses.includes('APPROVED')) return 'APPROVED';
  return 'PENDING';
};

const DRIVER_DOCUMENTS_KEY = 'driver_documents_v1';
const DRIVER_REVIEWS_KEY = 'driver_reviews_v1';

export const DRIVER_DOCUMENT_TYPES = [
  { key: 'aadhar', apiType: 'AADHAR', label: 'Aadhar' },
  { key: 'dl', apiType: 'DL', label: 'Driving License' },
  { key: 'rc', apiType: 'RC', label: 'Vehicle RC' },
];

export const getDriverDocumentApiType = (documentKey) => (
  DRIVER_DOCUMENT_TYPES.find((documentType) => documentType.key === documentKey)?.apiType || String(documentKey || '').toUpperCase()
);

const normalizeDocumentRecord = (document = {}) => ({
  ...document,
  name: document.name || document.fileName || document.file_name || document.originalName || document.original_name || '',
  type: document.type || document.contentType || document.content_type || '',
  dataUrl: document.dataUrl || document.data_url || document.fileUrl || document.file_url || document.url || '',
  uploadedAt: document.uploadedAt || document.uploaded_at || document.createdAt || document.created_at || null,
});

export const normalizeDriverDocuments = (documents) => {
  if (!documents) {
    return {};
  }

  if (Array.isArray(documents)) {
    return documents.reduce((normalizedDocuments, document) => {
      const rawType = String(document.documentType || document.document_type || document.type || '').toUpperCase();
      const compact = rawType.replace(/[_ -]/g, '');
      const key = ['AADHAAR', 'AADHAR'].includes(compact) ? 'aadhar' : compact === 'DRIVINGLICENSE' ? 'dl' : compact === 'REGISTRATIONCERTIFICATE' ? 'rc' : compact.toLowerCase();

      if (['aadhar', 'dl', 'rc'].includes(key)) {
        normalizedDocuments[key] = normalizeDocumentRecord(document);
      }

      return normalizedDocuments;
    }, {});
  }

  return DRIVER_DOCUMENT_TYPES.reduce((normalizedDocuments, documentType) => {
    const document =
      documents[documentType.key] ||
      documents[documentType.apiType] ||
      (documentType.key === 'aadhar' ? documents.aadhaar || documents.AADHAAR : null);

    if (document) {
      normalizedDocuments[documentType.key] = normalizeDocumentRecord(document);
    }

    return normalizedDocuments;
  }, {});
};

export const normalizeDriverDocumentsResponse = (responseData) => {
  let current = responseData || {};
  let payload = {};
  for (let depth = 0; depth < 10; depth += 1) {
    payload = { ...payload, ...current };
    const nested = current.data || current.payload || current.result;
    if (!nested || typeof nested !== 'object') break;
    if (Array.isArray(nested)) { payload = nested; break; }
    current = nested;

  }

  if (!Array.isArray(payload)) { delete payload.data; delete payload.payload; delete payload.result; }
  const documentsSource =
    payload.documents ||
    payload.driverDocuments ||
    payload.uploadedDocuments ||
    payload.items ||
    payload.content ||
    (payload.documentType || payload.document_type ? [payload] : null) ||
    (Array.isArray(payload) ? payload : {});

  const approvalStatus = getDriverApprovalStatus(payload, true);

  return {
    documents: normalizeDriverDocuments(documentsSource),
    approvalKnown: ['driverDocumentStatus', 'approvalStatus', 'driverApprovalStatus'].some(key => Boolean(payload[key])) || typeof payload.driverDocumentsApproved === 'boolean' || ['isApproved', 'approved', 'documentsApproved'].some(key => typeof payload[key] === 'boolean'),
    approvalStatus,
    submissionStatus: payload.submissionStatus || (payload.submitted === true ? 'SUBMITTED' : 'NOT_SUBMITTED'),
    submittedAt: payload.submittedAt || null,
    approvedAt: payload.approvedAt || null,
    rejectedAt: payload.rejectedAt || null,
    reason:
      payload.approvalReason ||
      payload.rejectionReason ||
      payload.reason ||
      payload.message ||
      '',
  };
};

const readMap = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || {};
  } catch {
    localStorage.removeItem(key);
    return {};
  }
};

const writeMap = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const normalizeKeyPart = (value) => String(value || '').trim();

const getDriverKeys = (driver) => ([
  driver?.id,
  driver?.driverId,
  driver?.userId,
  driver?._id,
  driver?.email,
  driver?.phone,
  driver?.mobile,
  driver?.mobileNumber,
  driver?.phoneNumber,
])
  .map(normalizeKeyPart)
  .filter(Boolean);

const getDriverKey = (driver) => getDriverKeys(driver)[0] || '';

const findStoredValue = (storageKey, driver) => {
  const map = readMap(storageKey);
  const keys = getDriverKeys(driver);

  for (const key of keys) {
    if (map[key]) {
      return map[key];
    }
  }

  return null;
};

const writeStoredValueForDriver = (storageKey, driver, value) => {
  const keys = getDriverKeys(driver);

  if (keys.length === 0) {
    return value;
  }

  const map = readMap(storageKey);
  keys.forEach((key) => {
    map[key] = value;
  });
  writeMap(storageKey, map);

  return value;
};

export const getStoredDriverDocuments = (driver) => {
  return findStoredValue(DRIVER_DOCUMENTS_KEY, driver) || {};
};

export const getStoredDriverReview = (driver) => {
  return findStoredValue(DRIVER_REVIEWS_KEY, driver);
};

export const hasAllDriverDocuments = (documents = {}) => (
  DRIVER_DOCUMENT_TYPES.every((documentType) => Boolean(documents[documentType.key]?.name))
);

export const saveDriverDocumentsForReview = (driver, documents) => {
  const submittedAt = new Date().toISOString();
  const submittedDocuments = DRIVER_DOCUMENT_TYPES.reduce((nextDocuments, documentType) => {
    if (documents[documentType.key]) {
      nextDocuments[documentType.key] = {
        ...documents[documentType.key],
        uploadedAt: documents[documentType.key].uploadedAt || submittedAt,
      };
    }

    return nextDocuments;
  }, {});

  writeStoredValueForDriver(DRIVER_DOCUMENTS_KEY, driver, submittedDocuments);
  writeStoredValueForDriver(DRIVER_REVIEWS_KEY, driver, {
    status: 'PENDING',
    reason: '',
    submittedAt,
    updatedAt: submittedAt,
  });

  return submittedDocuments;
};

export const saveDriverReview = (driver, status, reason = '') => {
  if (!getDriverKey(driver)) {
    return null;
  }

  const review = {
    status,
    reason: reason.trim(),
    updatedAt: new Date().toISOString(),
  };

  return writeStoredValueForDriver(DRIVER_REVIEWS_KEY, driver, review);
};

export const enrichDriverWithStoredReview = (driver) => {
  const documents = getStoredDriverDocuments(driver);
  const review = getStoredDriverReview(driver);
  const hasStoredDocuments = Object.keys(documents).length > 0;

  return {
    ...driver,
    documents: hasStoredDocuments ? documents : driver.documents || {},
    documentReview: review,
    approvalStatus: review?.status || driver.approvalStatus || 'PENDING',
    rejectionReason: review?.status === 'REJECTED' ? review.reason : driver.rejectionReason || '',
  };
};
